// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AutocompleteProductsStyles } from "@rugby/Overrides/Widgets/Header/AutocompleteProducts";
import { screen } from "@rugby/XDL/Helpers";
import { CustomMediaQuery, ellipsis } from "@rugby/XDL/Utilities";
import { css } from "styled-components";

const MediaQuery = CustomMediaQuery();

// from OOTB with some changes
export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 22,
        color: "text.main",
        css: css`
            font-size: 22px;
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    container: {
        gap: 2,
        css: css`
            display: flex;
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 20px;
            max-height: 70vw;
            width: 100%;
            ${screen("lte", "md")`
                width: 100%;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                > * {
                    margin-bottom: 8px;
                }
            `}
            ${MediaQuery.lte(1184)} {
                width: 100%;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                > * {
                    margin-bottom: 8px;
                }
            }
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        css: css`
            margin-right: 8px;
            width: 116px;
            height: 92px;
        `,
    },
    image: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            img {
                width: auto;
                height: auto;
                max-height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 116px);
            justify-content: space-between;
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            size: 16,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                white-space: unset;

                letter-spacing: 0;
                line-height: 22px;
                ${ellipsis(2, 22)}

                ${screen("lte", "md")`
                    text-align: center;
                    margin-bottom: 16px;
                `}
            `,
        },
        color: "secondary",
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

interface AutocompleteProductsExternalStyle {
    gridItemWrapper?: GridItemProps;
    productsContainer?: GridContainerProps;
    headerContainerTitleStyle?: InjectableCss;
    shopAllProductsStyle?: LinkPresentationProps;
    imageContainer?: ClickablePresentationProps;
    productInfo?: InjectableCss;
    productAction?: {
        container: InjectableCss;
        qtyInput: TextFieldPresentationProps;
        addToCart: ButtonPresentationProps;
    };
}

const autocompleteProductsExternalStyle: AutocompleteProductsExternalStyle = {
    gridItemWrapper: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${MediaQuery.lte(1184)} {
                max-width: 100%;
                flex-basis: 100%;
            }
        `,
    } as GridItemProps,
    productsContainer: {
        css: css`
            max-height: calc(143px * 3);
        `,
    },
    headerContainerTitleStyle: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-bottom: 2px solid ${getColor("common.border")};
            padding-bottom: 8px;
            text-align: left;
            line-height: 26px;
            margin-bottom: 26px;

            flex-wrap: wrap;
        `,
    },
    shopAllProductsStyle: {
        color: "secondary",
        typographyProps: {
            size: 15,
            css: css`
                text-align: right;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    imageContainer: {
        css: css`
            width: 116px;
            height: 92px;

            ${screen("gt", "md")`
                margin-right: 8px;
            `}
        `,
    },
    productInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: wrap;
            width: calc(100% - 116px);
            ${screen("lte", "md")`
                justify-content: center;
                align-items: center;
            `}
        `,
    },
    productAction: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                ${screen("lte", "sm")`
                    justify-content: center;
                `}
            `,
        },
        addToCart: {
            size: 34,
            css: css`
                padding: 0 10px;
                margin-top: 8px;
                flex-grow: 1;
                max-width: 160px;
            `,
        },
        qtyInput: {
            cssOverrides: {
                formField: css`
                    margin-right: 10px;
                    height: 34px;
                    width: 66px;
                    margin-top: 8px;

                    input[type="number"] {
                        -moz-appearance: textfield;
                    }

                    ${screen("lte", "sm")`
                        // we need to override general input style (height = 50px;) in LogoLinksSearch widget.
                        &&&& {
                            width: 66px;
                            input {
                                height: 34px;
                            }
                        }
                    `}
                `,
                inputSelect: css`
                    height: 34px;
                `,
            },
        },
    },
};

export default { ...autocompleteProductsStyles, ...autocompleteProductsExternalStyle };
